import { cn } from '@foundation/utils/cn';
import { ReactNode } from 'react';

import {
  HeadlessToast,
  HeadlessToastAddOptions,
  HeadlessToastObject,
  HeadlessToastRootProps,
  useToastManager,
} from '@design-system/headless-ui';
import { CheckCircle, Close, Info, Warning } from '@design-system/icons';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export type ToastOptions = Omit<
  HeadlessToastAddOptions<Record<string, never>>,
  'type' | 'title' | 'description' | 'actionProps'
> & {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly actionLabel?: ReactNode;
  readonly onAction?: () => void;
};

export type UseToastReturnValue = {
  readonly success: (options: ToastOptions) => string;
  readonly error: (options: ToastOptions) => string;
  readonly warning: (options: ToastOptions) => string;
  readonly info: (options: ToastOptions) => string;
  readonly neutral: (options: ToastOptions) => string;
  readonly close: (toastId?: string) => void;
};

// Public API surface: app code never touches Base UI's toast manager directly.
export function useToast(): UseToastReturnValue {
  const { add, close } = useToastManager();

  const addToast =
    (type: ToastVariant) =>
    ({ actionLabel, onAction, ...options }: ToastOptions): string =>
      add({
        ...options,
        type,
        actionProps: onAction
          ? { children: actionLabel, onClick: onAction }
          : undefined,
      });

  return {
    success: addToast('success'),
    error: addToast('error'),
    warning: addToast('warning'),
    info: addToast('info'),
    neutral: addToast('neutral'),
    close,
  };
}

const VARIANT_ICONS = {
  success: CheckCircle,
  error: Info,
  warning: Warning,
  info: Info,
  neutral: Info,
} as const satisfies Record<ToastVariant, typeof Info>;

const VARIANT_STYLES = {
  success:
    'bg-pmui-bg-status-positive-subtle text-pmui-fg-status-positive-default',
  error:
    'bg-pmui-bg-status-negative-subtle text-pmui-fg-status-negative-default',
  warning:
    'bg-pmui-bg-status-warning-subtle text-pmui-fg-status-warning-default',
  info: 'bg-pmui-bg-status-info-subtle text-pmui-fg-status-info-default',
  neutral: 'bg-pmui-bg-status-neutral-subtle text-pmui-fg-primary',
} as const satisfies Record<ToastVariant, string>;

export type ToastPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';

// Which edges the toast swipes toward to dismiss — matches the corner it's anchored to.
const SWIPE_DIRECTIONS = {
  'bottom-right': ['down', 'right'],
  'bottom-left': ['down', 'left'],
  'top-right': ['up', 'right'],
  'top-left': ['up', 'left'],
} as const satisfies Record<
  ToastPosition,
  HeadlessToastRootProps['swipeDirection']
>;

const VIEWPORT_POSITION_STYLES = {
  'bottom-right':
    'top-auto right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(1rem+env(safe-area-inset-bottom))]',
  'bottom-left':
    'top-auto left-[calc(1rem+env(safe-area-inset-left))] bottom-[calc(1rem+env(safe-area-inset-bottom))]',
  'top-right':
    'bottom-auto right-[calc(1rem+env(safe-area-inset-right))] top-[calc(1rem+env(safe-area-inset-top))]',
  'top-left':
    'bottom-auto left-[calc(1rem+env(safe-area-inset-left))] top-[calc(1rem+env(safe-area-inset-top))]',
} as const satisfies Record<ToastPosition, string>;

// Each string is kept fully literal (no template interpolation) so Tailwind's static
// scanner can find the arbitrary-value classes — ported per-corner from the Base UI docs'
// "hero" (bottom-anchored) and "position" (top-anchored) Tailwind demos.
const ROOT_POSITION_STYLES = {
  'bottom-right': cn(
    '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]',
    'right-0 bottom-0 left-auto mr-0 origin-bottom',
    'after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[""]',
    '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]',
    'data-starting-style:[transform:translateY(150%)]',
    '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
  ),
  'bottom-left': cn(
    '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]',
    'left-0 bottom-0 right-auto ml-0 origin-bottom',
    'after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[""]',
    '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]',
    'data-starting-style:[transform:translateY(150%)]',
    '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
  ),
  'top-right': cn(
    '[--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]',
    'right-0 top-0 left-auto mr-0 origin-top',
    'after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[""]',
    '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]',
    'data-starting-style:[transform:translateY(-150%)]',
    '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
  ),
  'top-left': cn(
    '[--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]',
    'left-0 top-0 right-auto ml-0 origin-top',
    'after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[""]',
    '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]',
    'data-starting-style:[transform:translateY(-150%)]',
    '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
  ),
} as const satisfies Record<ToastPosition, string>;

function ToastItem({
  toast,
  position,
}: {
  readonly toast: HeadlessToastObject<Record<string, never>>;
  readonly position: ToastPosition;
}) {
  const variant = (toast.type as ToastVariant | undefined) ?? 'neutral';
  const Icon = VARIANT_ICONS[variant];

  return (
    <HeadlessToast.Root
      toast={toast}
      swipeDirection={SWIPE_DIRECTIONS[position]}
      className={cn(
        // Local vars driving the collapsed (peek + shrink) and expanded (offset-y) positions —
        // ported verbatim from the Base UI docs' "Stacking and animations" Tailwind demo.
        '[--gap:0.75rem] [--peek:0.75rem]',
        '[--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
        '[--height:var(--toast-frontmost-height,var(--toast-height))]',
        'absolute w-full',
        'z-[calc(1000-var(--toast-index))] h-[var(--height)] data-expanded:h-[var(--toast-height)]',
        'rounded-sm shadow-lg select-none',
        '[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]',
        ROOT_POSITION_STYLES[position],
        'data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))]',
        'data-ending-style:opacity-0 data-limited:opacity-0',
        'data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-expanded:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'data-expanded:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-expanded:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-expanded:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        VARIANT_STYLES[variant],
      )}
    >
      <HeadlessToast.Content className="flex h-full items-start gap-2 overflow-hidden p-4 transition-opacity duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100">
        <Icon
          size={24}
          className="shrink-0"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {toast.title && (
            <HeadlessToast.Title className="text-md leading-6 font-semibold tracking-tight">
              {toast.title}
            </HeadlessToast.Title>
          )}
          {toast.description && (
            <HeadlessToast.Description className="text-md leading-6 tracking-tight">
              {toast.description}
            </HeadlessToast.Description>
          )}
          {toast.actionProps && (
            // Action's onClick/children come from toast.actionProps via context — don't spread it here too, or it double-fires.
            <HeadlessToast.Action className="inline-flex min-h-11 w-fit cursor-pointer items-center border-b border-current text-md leading-6" />
          )}
        </div>

        <HeadlessToast.Close
          aria-label="Close"
          data-testid="ds-toast-close"
          className="-m-2 -mt-2 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
        >
          <Close size={16} />
        </HeadlessToast.Close>
      </HeadlessToast.Content>
    </HeadlessToast.Root>
  );
}

function ToastList({ position }: { readonly position: ToastPosition }) {
  const { toasts } = useToastManager();
  return (
    <>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          position={position}
        />
      ))}
    </>
  );
}

export type ToastProps = {
  /** Which screen corner the stack is anchored to. @default 'bottom-right' */
  readonly position?: ToastPosition;
  readonly 'data-testid'?: string;
};

// Mount once near the app root, inside <ToastProvider>.
export function Toast({
  position = 'bottom-right',
  'data-testid': dataTestId,
}: ToastProps) {
  return (
    <HeadlessToast.Viewport
      className={cn(
        'z-50 mx-auto w-[calc(100vw-2rem)] max-w-[400px]',
        VIEWPORT_POSITION_STYLES[position],
      )}
      data-testid={dataTestId}
    >
      <ToastList position={position} />
    </HeadlessToast.Viewport>
  );
}

export const ToastProvider = HeadlessToast.Provider;
